import { Box, Typography, useTheme } from '@mui/material';
import { t } from 'i18next';
import { useAppSelector } from '../../../app/hooks';
import DownloadTableButton from '../../dataGridTables/DownloadTableButton';
import {
  selectAPIExplorerNumRowsSelected,
  selectEndpoint,
  selectFullURL,
} from '../apiExplorerSlice';
import APIExplorerSearchBar from './APIExplorerSearchBar';
import APIRawAndFilteredCountDisplay from './APIRawAndFilteredCountDisplay';
import EndpointDropdown from './EndpointDropdown';
import EnvironmentDropdown from './EnvironmentDropdown';

interface Props {
  onDownloadClick: () => void;
}

const APIExplorerHeader: React.FC<Props> = ({ onDownloadClick }) => {
  const theme = useTheme();
  const numRowsSelected = useAppSelector(selectAPIExplorerNumRowsSelected);
  const fullURL = useAppSelector(selectFullURL);
  const endpoint = useAppSelector(selectEndpoint);
  const noEndpointSelected = endpoint === '';

  return (
    <Box sx={{ width: '100%' }}>
      <Box
        sx={{
          width: '100%',
          p: '1.25rem 1.5rem 0.75rem 1.5rem',
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <Typography
            sx={{
              fontSize: '1.125rem',
              fontWeight: 700,
              color: theme.colors.canopy.content.primary,
            }}
          >
            {t('API Explorer')}
          </Typography>
        </Box>
        <DownloadTableButton
          onDownloadClick={onDownloadClick}
          numRowsSelected={numRowsSelected}
        />
      </Box>
      <Box
        sx={{
          width: '100%',
          p: '1rem 1.5rem',
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'center',
          gap: '1.5rem',
        }}
      >
        <ItemWithLabel label='Environment'>
          <EnvironmentDropdown />
        </ItemWithLabel>
        <ItemWithLabel label='Endpoint'>
          <EndpointDropdown />
        </ItemWithLabel>
        <ItemWithLabel label='URL'>
          <Typography variant='body1'>
            {noEndpointSelected ? t('Select an endpoint') : fullURL}
          </Typography>
        </ItemWithLabel>
      </Box>
      <Box
        sx={{
          width: '100%',
          p: '0.75rem 1.5rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '1rem',
        }}
      >
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '1rem',
          }}
        >
          <APIExplorerSearchBar />
          <APIRawAndFilteredCountDisplay />
        </Box>
      </Box>
    </Box>
  );
};

export default APIExplorerHeader;

interface FieldSectionProps {
  children: React.ReactElement;
  label: string;
}

const ItemWithLabel: React.FC<FieldSectionProps> = ({ children, label }) => {
  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: '0.5rem',
      }}
    >
      <Typography variant='body2' sx={{ ml: '0.1rem' }}>
        {t(label)}
      </Typography>
      {children}
    </Box>
  );
};
